'use client';

import { useSession } from '@/providers/enhanced-session';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import {
    CONVERSATIONS_PAGE_SIZE,
    DispatchType,
    QUERY_CONSTANTS,
    QUERY_KEYS,
    ROLE_TYPES,
    STREAMING_EVENT_TYPES,
    UserFeedbackFilter,
} from '../constants';
import { studioAIService } from '../services';
import type {
    ChatMessage,
    ConversationShareDetails,
    FeedbackDto,
    PaginatedReportsResponse,
    ShareConversationRequest,
    ShareConversationResponse,
    StudioAIConversation,
    StudioAIFolder,
    StudioAIReport,
} from '../types';
import type { StudioAIAction } from './useChatReducer';

export interface HistoryOperationsConfig {
    dispatch: React.Dispatch<StudioAIAction>;
    messages: ChatMessage[];
}

export function useHistoryOperations(config: HistoryOperationsConfig) {
    const { dispatch, messages } = config;
    const queryClient = useQueryClient();
    const { data: session } = useSession();

    // Unaffiliated (freemium) users use the restricted DANI experience and have no
    // feedback inbox; their requests to the access-guarded /studio-ai/feedback endpoint
    // would 403 (and the axios interceptor turns that into a sign-in redirect), so skip it.
    const isUnaffiliated = session?.user?.isUnaffiliated ?? false;

    const [historyQueriesEnabled, setHistoryQueriesEnabled] = useState(false);

    // Conversation pagination and search
    const [conversationLimit, setConversationLimit] = useState<number>(CONVERSATIONS_PAGE_SIZE);
    const [conversationPage, setConversationPage] = useState<number>(1);
    const [conversationSearchTerm, setConversationSearchTerm] = useState('');

    // Report pagination and search
    const [reportLimit, setReportLimit] = useState<number>(CONVERSATIONS_PAGE_SIZE);
    const [reportPage, setReportPage] = useState<number>(1);
    const [reportSearchTerm, setReportSearchTerm] = useState('');

    const conversationsQueryParams = {
        limit: conversationSearchTerm ? undefined : conversationLimit,
        search: conversationSearchTerm || undefined,
        page: conversationSearchTerm ? undefined : conversationPage,
    };

    const conversationsQuery = useQuery({
        queryKey: QUERY_KEYS.CONVERSATIONS(conversationsQueryParams),
        queryFn: () => studioAIService.getConversations(conversationsQueryParams),
        staleTime: QUERY_CONSTANTS.CONVERSATIONS_STALE_TIME_MS,
        gcTime: QUERY_CONSTANTS.CONVERSATIONS_GC_TIME_MS,
        enabled: historyQueriesEnabled,
    });

    const foldersQuery = useQuery({
        queryKey: QUERY_KEYS.FOLDERS,
        queryFn: () => studioAIService.getFolders(),
        staleTime: QUERY_CONSTANTS.CONVERSATIONS_STALE_TIME_MS,
        gcTime: QUERY_CONSTANTS.CONVERSATIONS_GC_TIME_MS,
        enabled: historyQueriesEnabled,
    });

    const allFolders = foldersQuery.data ?? [];
    const conversationFolders = useMemo(() => {
        const seen = new Set<number>();
        return allFolders.filter((f) => {
            if (f.folderType && f.folderType !== 'conversation') return false;
            if (seen.has(f.id)) return false;
            seen.add(f.id);
            return true;
        });
    }, [allFolders]);
    const reportTypeFolders = useMemo(() => {
        const seen = new Set<number>();
        return allFolders.filter((f) => {
            if (f.folderType !== 'report') return false;
            if (seen.has(f.id)) return false;
            seen.add(f.id);
            return true;
        });
    }, [allFolders]);

    // Per-folder pagination params, keyed by folderId
    const [folderConversationParams, setFolderConversationParams] = useState<
        Record<number, { page: number; limit: number }>
    >({});
    const [folderReportParams, setFolderReportParams] = useState<
        Record<number, { page: number; limit: number }>
    >({});

    const getFolderConvParams = (folderId: number) =>
        folderConversationParams[folderId] ?? { page: 1, limit: CONVERSATIONS_PAGE_SIZE };
    const getFolderReportParamsFn = (folderId: number) =>
        folderReportParams[folderId] ?? { page: 1, limit: CONVERSATIONS_PAGE_SIZE };

    const folderConversationQueries = useQueries({
        queries: conversationFolders.map((folder) => {
            const params = getFolderConvParams(folder.id);
            const folderParams = {
                folderId: folder.id,
                page: conversationSearchTerm ? undefined : params.page,
                limit: conversationSearchTerm ? undefined : params.limit,
                search: conversationSearchTerm || undefined,
            };
            return {
                queryKey: QUERY_KEYS.CONVERSATIONS(folderParams),
                queryFn: () => studioAIService.getConversations(folderParams),
                staleTime: QUERY_CONSTANTS.CONVERSATIONS_STALE_TIME_MS,
                gcTime: QUERY_CONSTANTS.CONVERSATIONS_GC_TIME_MS,
                enabled: historyQueriesEnabled,
            };
        }),
    });

    const folderReportQueries = useQueries({
        queries: reportTypeFolders.map((folder) => {
            const params = getFolderReportParamsFn(folder.id);
            return {
                queryKey: QUERY_KEYS.REPORTS({
                    folderId: folder.id,
                    page: params.page,
                    limit: params.limit,
                }),
                queryFn: () => studioAIService.getReports({ folderId: folder.id, ...params }),
                staleTime: QUERY_CONSTANTS.CONVERSATIONS_STALE_TIME_MS,
                gcTime: QUERY_CONSTANTS.CONVERSATIONS_GC_TIME_MS,
                enabled: historyQueriesEnabled,
            };
        }),
    });

    const folderedConversations: Record<number, StudioAIConversation[]> = {};
    const folderConversationTotals: Record<number, { total: number; totalPages: number }> = {};
    conversationFolders.forEach((folder, index) => {
        const queryData = folderConversationQueries[index]?.data;
        const params = getFolderConvParams(folder.id);
        folderedConversations[folder.id] = queryData?.data ?? [];
        // Use folder.conversationCount as authoritative total; when searching use result length
        // since pagination is disabled and the count reflects matched items only
        const total = conversationSearchTerm
            ? (queryData?.data?.length ?? 0)
            : folder.conversationCount;
        folderConversationTotals[folder.id] = {
            total,
            totalPages: Math.max(1, Math.ceil(total / Math.max(1, params.limit))),
        };
    });

    const folderedReports: Record<number, StudioAIReport[]> = {};
    const folderReportTotals: Record<number, { total: number; totalPages: number }> = {};
    reportTypeFolders.forEach((folder, index) => {
        const queryData = folderReportQueries[index]?.data;
        const params = getFolderReportParamsFn(folder.id);
        folderedReports[folder.id] = queryData?.data ?? [];
        const total = folder.conversationCount;
        folderReportTotals[folder.id] = {
            total,
            totalPages: Math.max(1, Math.ceil(total / Math.max(1, params.limit))),
        };
    });

    const setFolderConversationPage = useCallback(
        (folderId: number, page: number) => {
            setFolderConversationParams((prev) => ({
                ...prev,
                [folderId]: { ...getFolderConvParams(folderId), page },
            }));
        },
        [folderConversationParams],
    );

    const setFolderConversationLimit = useCallback((folderId: number, limit: number) => {
        setFolderConversationParams((prev) => ({
            ...prev,
            [folderId]: { page: 1, limit },
        }));
    }, []);

    const setFolderReportPage = useCallback(
        (folderId: number, page: number) => {
            setFolderReportParams((prev) => ({
                ...prev,
                [folderId]: { ...getFolderReportParamsFn(folderId), page },
            }));
        },
        [folderReportParams],
    );

    const setFolderReportLimit = useCallback((folderId: number, limit: number) => {
        setFolderReportParams((prev) => ({
            ...prev,
            [folderId]: { page: 1, limit },
        }));
    }, []);

    const sharedWithMeQuery = useQuery({
        queryKey: QUERY_KEYS.SHARED_WITH_ME,
        queryFn: () => studioAIService.getSharedWithMe(),
        staleTime: QUERY_CONSTANTS.CONVERSATIONS_STALE_TIME_MS,
        enabled: historyQueriesEnabled && !!session?.user,
    });

    const orgUsersQuery = useQuery({
        queryKey: QUERY_KEYS.ORGANIZATION_USERS,
        queryFn: () => studioAIService.getOrganizationUsers(),
        staleTime: QUERY_CONSTANTS.CONVERSATIONS_GC_TIME_MS,
        enabled: historyQueriesEnabled && !!session?.user,
    });

    const allUserFeedbackQuery = useQuery({
        queryKey: QUERY_KEYS.USER_FEEDBACK(UserFeedbackFilter.ALL),
        queryFn: () => studioAIService.getUserFeedback(UserFeedbackFilter.ALL),
        staleTime: QUERY_CONSTANTS.CONVERSATIONS_STALE_TIME_MS,
        gcTime: QUERY_CONSTANTS.CONVERSATIONS_GC_TIME_MS,
        enabled: !isUnaffiliated,
    });

    const [userFeedbackFilter, setUserFeedbackFilter] = useState<UserFeedbackFilter>(
        UserFeedbackFilter.ALL,
    );

    const userFeedbackQuery = useQuery({
        queryKey: QUERY_KEYS.USER_FEEDBACK(userFeedbackFilter),
        queryFn: () => studioAIService.getUserFeedback(userFeedbackFilter),
        staleTime: QUERY_CONSTANTS.CONVERSATIONS_STALE_TIME_MS,
        gcTime: QUERY_CONSTANTS.CONVERSATIONS_GC_TIME_MS,
        enabled: historyQueriesEnabled,
    });

    const unreadFeedbackCountQuery = useQuery({
        queryKey: QUERY_KEYS.FEEDBACK_COUNT,
        queryFn: () => studioAIService.getUnreadFeedbackCount(),
        staleTime: QUERY_CONSTANTS.CONVERSATIONS_STALE_TIME_MS,
        gcTime: QUERY_CONSTANTS.CONVERSATIONS_GC_TIME_MS,
        enabled: historyQueriesEnabled,
    });

    const dataRefreshDatesQuery = useQuery({
        queryKey: QUERY_KEYS.DATA_REFRESH_DATES,
        queryFn: () => studioAIService.getDataRefreshDates(),
        staleTime: QUERY_CONSTANTS.CONVERSATIONS_STALE_TIME_MS,
        gcTime: QUERY_CONSTANTS.CONVERSATIONS_GC_TIME_MS,
    });

    const reportsQueryParams = {
        limit: reportSearchTerm ? undefined : reportLimit,
        search: reportSearchTerm || undefined,
        page: reportSearchTerm ? undefined : reportPage,
    };

    const reportsQuery = useQuery<PaginatedReportsResponse>({
        queryKey: QUERY_KEYS.REPORTS(reportsQueryParams),
        queryFn: () => studioAIService.getReports(reportsQueryParams),
        staleTime: QUERY_CONSTANTS.CONVERSATIONS_STALE_TIME_MS,
        gcTime: QUERY_CONSTANTS.CONVERSATIONS_GC_TIME_MS,
        enabled: historyQueriesEnabled,
    });

    // Mutations
    const deleteConversationMutation = useMutation({
        mutationFn: (conversationId: string) => studioAIService.deleteConversation(conversationId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'conversations'] });
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'reports'] });
        },
        onError: (error) => {
            dispatch({ type: DispatchType.SetError, payload: error.message });
        },
    });

    const renameConversationMutation = useMutation({
        mutationFn: ({ conversationId, newTitle }: { conversationId: string; newTitle: string }) =>
            studioAIService.renameConversation(conversationId, newTitle),
        onSuccess: (_, { conversationId, newTitle }) => {
            dispatch({
                type: DispatchType.UpdateConversation,
                payload: { conversationId, updates: { title: newTitle } },
            });
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'conversations'] });
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'reports'] });
        },
        onError: (error) => {
            dispatch({ type: DispatchType.SetError, payload: error.message });
        },
    });

    const submitFeedbackMutation = useMutation({
        mutationFn: ({
            feedback,
            conversationContext,
        }: {
            feedback: FeedbackDto;
            conversationContext: { userMessage?: string; aiResponse?: string };
        }) =>
            studioAIService.submitFeedback(
                feedback,
                conversationContext,
                session?.user,
                session?.org,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'conversations'] });
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'user-feedback'] });
        },
        onError: (error) => {
            dispatch({ type: DispatchType.SetError, payload: error.message });
        },
    });

    const markFeedbackAsReadMutation = useMutation({
        mutationFn: (feedbackId: number) => studioAIService.markFeedbackAsRead(feedbackId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'user-feedback'] });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FEEDBACK_COUNT });
        },
        onError: (error) => {
            dispatch({ type: DispatchType.SetError, payload: error.message });
        },
    });

    const createFolderMutation = useMutation({
        mutationFn: ({ name, folderType }: { name: string; folderType?: string }) =>
            studioAIService.createFolder(name, folderType),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FOLDERS });
        },
        onError: (error) => {
            dispatch({ type: DispatchType.SetError, payload: error.message });
        },
    });

    const renameFolderMutation = useMutation({
        mutationFn: ({ folderId, newName }: { folderId: number; newName: string }) =>
            studioAIService.renameFolder(folderId, newName),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FOLDERS });
        },
        onError: (error) => {
            dispatch({ type: DispatchType.SetError, payload: error.message });
        },
    });

    const deleteFolderMutation = useMutation({
        mutationFn: (folderId: number) => studioAIService.deleteFolder(folderId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FOLDERS });
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'conversations'] });
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'reports'] });
        },
        onError: (error) => {
            dispatch({ type: DispatchType.SetError, payload: error.message });
        },
    });

    const moveConversationToFolderMutation = useMutation({
        mutationFn: ({
            conversationId,
            folderId,
        }: {
            conversationId: string;
            folderId: number | null;
        }) => studioAIService.moveConversationToFolder(conversationId, folderId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'conversations'] });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.FOLDERS });
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'reports'] });
        },
        onError: (error) => {
            dispatch({ type: DispatchType.SetError, payload: error.message });
        },
    });

    const shareConversationMutation = useMutation({
        mutationFn: ({
            conversationId,
            request,
        }: {
            conversationId: string;
            request: ShareConversationRequest;
        }) => studioAIService.shareConversation(conversationId, request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SHARED_WITH_ME });
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'conversations'] });
        },
    });

    const revokeShareMutation = useMutation({
        mutationFn: (shareId: number) => studioAIService.revokeShare(shareId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SHARED_WITH_ME });
            queryClient.invalidateQueries({ queryKey: ['studio-ai', 'conversations'] });
        },
    });

    // Wrapped actions
    const deleteConversation = useCallback(
        (conversationId: string) => deleteConversationMutation.mutateAsync(conversationId),
        [deleteConversationMutation],
    );

    const renameConversation = useCallback(
        (conversationId: string, newTitle: string) =>
            renameConversationMutation.mutateAsync({ conversationId, newTitle }),
        [renameConversationMutation],
    );

    const submitFeedback = useCallback(
        (feedback: FeedbackDto) => {
            const targetMessage = messages.find((msg) => msg.id === feedback.messageId);

            let userMessage: ChatMessage | undefined;
            if (targetMessage?.role === ROLE_TYPES.ASSISTANT) {
                const baseId = targetMessage.id.replace(/_ai$/, '');
                userMessage = messages.find(
                    (msg) => msg.id === `${baseId}_user` && msg.role === ROLE_TYPES.USER,
                );
                if (!userMessage) {
                    const targetIndex = messages.findIndex((msg) => msg.id === feedback.messageId);
                    if (targetIndex > 0) {
                        for (let i = targetIndex - 1; i >= 0; i--) {
                            if (messages[i].role === ROLE_TYPES.USER) {
                                userMessage = messages[i];
                                break;
                            }
                        }
                    }
                }
            }

            const aiResponse =
                targetMessage?.role === ROLE_TYPES.ASSISTANT ? targetMessage : undefined;

            const getMessageText = (message: ChatMessage | undefined): string => {
                if (!message) return '';
                if (typeof message.content === 'string') return message.content;
                if (Array.isArray(message.content)) {
                    const textContent = message.content.find(
                        (c) => c.type === STREAMING_EVENT_TYPES.TEXT,
                    );
                    return textContent?.text || '';
                }
                return '';
            };

            const getAIResponseText = (message: ChatMessage | undefined): string => {
                if (!message) return '';
                if (typeof message.content === 'string') return message.content;
                if (Array.isArray(message.content)) {
                    return message.content
                        .filter((c) => c.type === STREAMING_EVENT_TYPES.TEXT && c.text)
                        .map((c) => c.text)
                        .join('\n');
                }
                return '';
            };

            return submitFeedbackMutation.mutateAsync({
                feedback,
                conversationContext: {
                    userMessage: getMessageText(userMessage),
                    aiResponse: getAIResponseText(aiResponse),
                },
            });
        },
        [submitFeedbackMutation, messages],
    );

    // Stable across renders: ShareModal re-fetches whenever this changes, and the chat re-renders
    // on every streamed chunk (BT-2015).
    const getConversationShares = useCallback(
        (conversationId: string): Promise<ConversationShareDetails> =>
            studioAIService.getConversationShares(conversationId),
        [],
    );

    const searchConversations = useCallback((search: string) => {
        setConversationSearchTerm(search);
        setConversationPage(1);
    }, []);

    const handleSetConversationLimit = useCallback((limit: number) => {
        setConversationLimit(limit);
        setConversationPage(1);
    }, []);

    const searchReports = useCallback((search: string) => {
        setReportSearchTerm(search);
        setReportPage(1);
    }, []);

    const handleSetReportLimit = useCallback((limit: number) => {
        setReportLimit(limit);
        setReportPage(1);
    }, []);

    const totalUnfiledConversations = conversationsQuery.data?.totalUnfiled ?? 0;
    const safeConversationLimit = Math.max(1, conversationLimit);
    const totalPages = Math.max(1, Math.ceil(totalUnfiledConversations / safeConversationLimit));

    const totalUnfiledReports = reportsQuery.data?.totalUnfiled ?? 0;
    const safeReportLimit = Math.max(1, reportLimit);
    const reportTotalPages = Math.max(1, Math.ceil(totalUnfiledReports / safeReportLimit));

    const folderConversationFetching: Record<number, boolean> = {};
    conversationFolders.forEach((folder, index) => {
        folderConversationFetching[folder.id] =
            folderConversationQueries[index]?.isFetching ?? false;
    });

    const folderReportFetching: Record<number, boolean> = {};
    reportTypeFolders.forEach((folder, index) => {
        folderReportFetching[folder.id] = folderReportQueries[index]?.isFetching ?? false;
    });

    return {
        // Queries
        conversationsQuery,
        foldersQuery,
        folderedConversations,
        folderedReports,
        folderConversationTotals,
        folderReportTotals,
        folderConversationParams,
        folderReportParams,
        setFolderConversationPage,
        setFolderConversationLimit,
        setFolderReportPage,
        setFolderReportLimit,
        conversationsIsFetching: conversationsQuery.isFetching,
        reportsIsFetching: reportsQuery.isFetching,
        folderConversationFetching,
        folderReportFetching,
        sharedWithMeQuery,
        orgUsersQuery,
        allUserFeedbackQuery,
        userFeedbackQuery,
        unreadFeedbackCountQuery,
        dataRefreshDatesQuery,
        reportsQuery,
        deleteConversationMutation,

        // History query control
        historyQueriesEnabled,
        setHistoryQueriesEnabled,

        // Feedback
        userFeedbackFilter,
        setUserFeedbackFilter,
        markFeedbackAsRead: async (feedbackId: number): Promise<void> => {
            await markFeedbackAsReadMutation.mutateAsync(feedbackId);
        },
        refetchUserFeedback: async () => {
            const result = await userFeedbackQuery.refetch();
            return result.data || [];
        },
        submitFeedback,

        // Conversation actions
        deleteConversation,
        renameConversation,
        refetchConversations: () => conversationsQuery.refetch(),

        // Search / pagination
        searchConversations,
        conversationSearchTerm,
        conversationPage,
        conversationLimit,
        totalUnfiledConversations,
        totalPages,
        setConversationPage,
        setConversationLimit: handleSetConversationLimit,

        // Folder actions
        createFolder: (name: string, folderType?: string): Promise<StudioAIFolder> =>
            createFolderMutation.mutateAsync({ name, folderType }),
        renameFolder: (folderId: number, newName: string) =>
            renameFolderMutation.mutateAsync({ folderId, newName }),
        deleteFolder: (folderId: number) => deleteFolderMutation.mutateAsync(folderId),
        moveConversationToFolder: (conversationId: string, folderId: number | null) =>
            moveConversationToFolderMutation.mutateAsync({ conversationId, folderId }),
        refetchFolders: () => foldersQuery.refetch(),

        // Report search / pagination
        searchReports,
        reportSearchTerm,
        reportPage,
        reportLimit,
        totalUnfiledReports,
        reportTotalPages,
        setReportPage,
        setReportLimit: handleSetReportLimit,

        // Sharing
        shareConversation: (
            conversationId: string,
            request: ShareConversationRequest,
        ): Promise<ShareConversationResponse> =>
            shareConversationMutation.mutateAsync({ conversationId, request }),
        getConversationShares,
        revokeShare: (shareId: number) => revokeShareMutation.mutateAsync(shareId),
        refetchSharedWithMe: () => sharedWithMeQuery.refetch(),
    };
}
